if (!customElements.get('cart-discount')) {
  customElements.define(
    'cart-discount',
    class CartDiscount extends HTMLElement {
      constructor() {
        super();

        this.onSubmit = this.onSubmit.bind(this);
        this.onRemove = this.onRemove.bind(this);
      }

      connectedCallback() {
        this.form = this.querySelector('form');
        this.input = this.querySelector('[name="discount"]');
        this.errorMessage = this.querySelector('.cart-discount__error');
        this.submitButton = this.querySelector('[type="submit"]');

        if (this.form && this.form.dataset.discountInit !== 'true') {
          this.form.dataset.discountInit = 'true';
          this.form.addEventListener('submit', this.onSubmit);
        }
        this.bindRemoveButtons();
      }

      bindRemoveButtons() {
        this.querySelectorAll('.cart-discount__remove').forEach((button) => {
          if (button.dataset.discountRemoveInit === 'true') return;
          button.dataset.discountRemoveInit = 'true';
          button.addEventListener('click', this.onRemove);
        });
      }

      getSectionsToRender() {
        if (this.dataset.context === 'drawer') {
          return [
            {
              id: 'cart-drawer',
              section: 'cart-drawer',
            },
            {
              id: 'cart-icon-bubble',
              section: 'cart-icon-bubble',
              selector: '.shopify-section',
            },
          ];
        }

        return [
          {
            id: 'main-cart-items',
            section: document.getElementById('main-cart-items').dataset.id,
            selector: '.js-contents',
          },
          {
            id: 'main-cart-footer',
            section: document.getElementById('main-cart-footer').dataset.id,
            selector: '.js-contents',
          },
          {
            id: 'cart-icon-bubble',
            section: 'cart-icon-bubble',
            selector: '.shopify-section',
          },
        ];
      }

      onSubmit(event) {
        event.preventDefault();
        const code = this.input?.value.trim();
        if (!code) {
          this.showError(window.cartStrings.discountInvalid);
          return;
        }

        if (this.getExistingCodes().length > 0) return;

        this.updateDiscount(code, code);
      }

      onRemove(event) {
        event.preventDefault();
        event.stopPropagation();
        this.updateDiscount('', null);
      }

      getExistingCodes() {
        return Array.from(this.querySelectorAll('[data-discount-code]')).map(
          (element) => element.dataset.discountCode
        );
      }

      updateDiscount(discountValue, submittedCode) {
        this.clearError();
        this.setLoading(true);

        const body = JSON.stringify({
          discount: discountValue,
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname,
        });

        fetch(`${routes.cart_update_url}`, { ...fetchConfig(), ...{ body } })
          .then((response) => response.text())
          .then((state) => {
            const parsedState = JSON.parse(state);

            if (parsedState.status) {
              this.showError(window.cartStrings.error);
              return;
            }

            if (submittedCode && !this.isDiscountApplied(parsedState, submittedCode)) {
              this.showError(window.cartStrings.discountInvalid);
              return;
            }

            this.renderSections(parsedState);
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-discount', cartData: parsedState });
            if (typeof window.trsInitCartOfferCopy === 'function') {
              window.trsInitCartOfferCopy();
            }
          })
          .catch(() => {
            this.showError(window.cartStrings.error);
          })
          .finally(() => {
            this.setLoading(false);
          });
      }

      isDiscountApplied(cart, code) {
        if (cart.discount_codes?.length) {
          return cart.discount_codes.some(
            (discount) => discount.code.toLowerCase() === code.toLowerCase() && discount.applicable
          );
        }

        if (cart.cart_level_discount_applications?.length) {
          return cart.cart_level_discount_applications.some(
            (discount) => discount.title.toLowerCase() === code.toLowerCase()
          );
        }

        return cart.items?.some((item) =>
          item.discounts?.some((discount) => discount.title.toLowerCase() === code.toLowerCase())
        );
      }

      renderSections(parsedState) {
        if (this.dataset.context === 'drawer') {
          const sectionHtml = parsedState.sections?.['cart-drawer'];
          if (!sectionHtml) return;

          const html = new DOMParser().parseFromString(sectionHtml, 'text/html');
          ['cart-drawer-items', '.cart-drawer__footer'].forEach((selector) => {
            const targetElement = document.querySelector(selector);
            const sourceElement = html.querySelector(selector);
            if (targetElement && sourceElement) {
              targetElement.replaceWith(sourceElement);
            }
          });

          const cartIconSection = parsedState.sections?.['cart-icon-bubble'];
          if (cartIconSection) {
            const iconElement = document.getElementById('cart-icon-bubble');
            if (iconElement) {
              iconElement.innerHTML = this.getSectionInnerHTML(cartIconSection, '.shopify-section');
            }
          }

          return;
        }

        this.getSectionsToRender().forEach((section) => {
          const sectionHtml = parsedState.sections?.[section.section];
          if (!sectionHtml) return;

          const elementToReplace =
            document.getElementById(section.id)?.querySelector(section.selector) ||
            document.getElementById(section.id);

          if (elementToReplace) {
            elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
          }
        });
      }

      getSectionInnerHTML(html, selector = '.shopify-section') {
        return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
      }

      showError(message) {
        if (!this.errorMessage) return;
        this.errorMessage.textContent = message;
        this.errorMessage.hidden = false;
        this.input?.setAttribute('aria-invalid', 'true');
        const details = this.errorMessage.closest('details');
        if (details) details.open = true;
      }

      clearError() {
        if (!this.errorMessage) return;
        this.errorMessage.textContent = '';
        this.errorMessage.hidden = true;
        this.input?.removeAttribute('aria-invalid');
      }

      setLoading(isLoading) {
        if (!this.submitButton || !this.input) return;
        this.submitButton.toggleAttribute('disabled', isLoading);
        this.submitButton.classList.toggle('loading', isLoading);
        this.input.toggleAttribute('disabled', isLoading);
      }
    }
  );
}

/* Script is included from both cart-drawer and main-cart-footer; guard against
   redeclaring top-level bindings when both are present (e.g. /cart page). */
if (!window.trsCartDiscountExtras) {
  window.trsCartDiscountExtras = true;

  let trsCartOfferCopyBound = false;

  function trsInitCartOfferCopy() {
    if (trsCartOfferCopyBound) return;
    trsCartOfferCopyBound = true;
    document.addEventListener('click', trsHandleCartOfferCopy);
  }

  // Expose for CartDiscount.updateDiscount callback (same script, first load).
  window.trsInitCartOfferCopy = trsInitCartOfferCopy;

  function trsHandleCartOfferCopy(event) {
    const button = event.target.closest('[data-copy-code][data-fill-discount]');
    if (!button) return;

    event.preventDefault();
    const code = button.dataset.copyCode;
    const inputId = button.dataset.fillDiscount;
    const label = button.querySelector('.cart-offer-banner__copy-label');
    const autoApply = button.dataset.autoApply === 'true';

    const setLabel = (text) => {
      if (label) label.textContent = text;
    };

    const input = document.getElementById(inputId);
    const cartDiscount = input?.closest('cart-discount');
    const hasAppliedCode = cartDiscount?.querySelector('.cart-discount__applied-badge');
    const details = input?.closest('details');

    if (details) details.open = true;

    if (autoApply && cartDiscount && !hasAppliedCode && input) {
      cartDiscount.clearError?.();
      input.value = code;
      setLabel('Applying…');
      if (typeof cartDiscount.updateDiscount === 'function') {
        cartDiscount.updateDiscount(code, code);
      } else {
        cartDiscount.querySelector('[type="submit"]')?.click();
      }
      setTimeout(() => setLabel('Apply'), 2500);
      return;
    }

    if (!hasAppliedCode && input) {
      cartDiscount?.clearError?.();
      input.value = code;
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.dispatchEvent(new Event('change', { bubbles: true }));
      input.focus();
      input.select();
    }

    const onCopied = () => {
      setLabel(window.cartStrings.copyCodeCopied);
      setTimeout(() => setLabel(window.cartStrings.copyCodeLabel || 'Apply'), 2000);
    };

    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(code).then(onCopied).catch(() => trsFallbackCopy(code, onCopied));
    } else {
      trsFallbackCopy(code, onCopied);
    }
  }

  function trsFallbackCopy(text, callback) {
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.style.cssText = 'position:fixed;opacity:0;pointer-events:none;';
    document.body.appendChild(textarea);
    textarea.focus();
    textarea.select();
    try {
      document.execCommand('copy');
      callback();
    } catch (error) {
      console.error(error);
    }
    document.body.removeChild(textarea);
  }

  document.addEventListener('DOMContentLoaded', trsInitCartOfferCopy);

  function trsGetDiscountCodesFromCart(cart) {
    const codes = new Set();

    if (Array.isArray(cart.discount_codes)) {
      cart.discount_codes.forEach((entry) => {
        if (entry.applicable && entry.code) codes.add(entry.code);
      });
    }

    if (Array.isArray(cart.cart_level_discount_applications)) {
      cart.cart_level_discount_applications.forEach((entry) => {
        if (entry.type === 'discount_code' && entry.title) codes.add(entry.title);
      });
    }

    return Array.from(codes);
  }

  function trsGetDiscountCodesFromDom() {
    return Array.from(document.querySelectorAll('[data-discount-code]'))
      .map((element) => element.dataset.discountCode)
      .filter(Boolean);
  }

  function trsGetPendingDiscountCodesFromDom() {
    return Array.from(document.querySelectorAll('cart-discount [name="discount"]'))
      .map((input) => input.value.trim())
      .filter(Boolean);
  }

  function trsBuildCheckoutUrl(codes) {
    const checkoutBase = window.routes?.checkout_url || '/checkout';
    const url = new URL(checkoutBase, window.location.origin);

    if (codes.length) {
      url.searchParams.set('discount', codes.join(','));
    }

    return `${url.pathname}${url.search}`;
  }

  async function trsGoToCheckout(button) {
    button.setAttribute('aria-disabled', 'true');
    button.classList.add('loading');

    try {
      const response = await fetch(`${routes.cart_url}.js`, { credentials: 'same-origin' });
      const cart = await response.json();
      let codes = trsGetDiscountCodesFromCart(cart);

      if (!codes.length) {
        codes = trsGetDiscountCodesFromDom();
      }
      if (!codes.length) {
        codes = trsGetPendingDiscountCodesFromDom();
      }

      window.location.assign(trsBuildCheckoutUrl(codes));
    } catch (error) {
      console.error(error);
      const formId = button.getAttribute('form');
      const form = formId ? document.getElementById(formId) : button.closest('form');
      if (form) {
        form.submit();
      } else {
        window.location.assign(window.routes?.checkout_url || '/checkout');
      }
    }
  }

  document.addEventListener('click', (event) => {
    const button = event.target.closest('button[name="checkout"].cart__checkout-button');
    if (!button || button.disabled || button.getAttribute('aria-disabled') === 'true') return;
    if (!button.closest('cart-drawer, #main-cart-footer')) return;

    event.preventDefault();
    trsGoToCheckout(button);
  });

  if (typeof subscribe === 'function') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event.source === 'cart-discount') return;
      trsInitCartOfferCopy();
    });
  }
}
